import axios from 'axios';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from '../../Api';
import { Store } from '../../Store';
import { getError } from '../../utils';

const ACTIONS = [
  { value: 'edit', label: 'Member edited' },
  { value: 'admin', label: 'Office edited' },
  { value: 'grant', label: 'Extra edits approved' },
  { value: 'reset', label: 'Counter reset' },
];
const SOURCES = ['web', 'app', 'admin'];
const PAGE_SIZES = [20, 50, 100];

const actionColor = (a) =>
  ({
    edit: { bg: '#dbeafe', color: '#1d4ed8' },
    admin: { bg: '#e0e7ff', color: '#3730a3' },
    grant: { bg: '#dcfce7', color: '#166534' },
    reset: { bg: '#fef3c7', color: '#92400e' },
  }[a] || { bg: '#f3f4f6', color: '#374151' });

/**
 * PROFILE EDIT HISTORY — every change anyone has made to any profile.
 *
 * The question staff actually arrive with is "who changed this account number,
 * and when", so the free-text box searches the old and new values as well as
 * the member and the staff member. Everything else narrows: which field, who
 * did it, from where, and between which dates.
 */
export default function ProfileEditHistoryScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${userInfo?.token}` }),
    [userInfo]
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const [fields, setFields] = useState([]);
  const [data, setData] = useState({ rows: [], page: 1, pages: 1, count: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({
    q: '',
    username: searchParams.get('username') || '',
    userId: searchParams.get('userId') || '',
    field: '',
    action: '',
    source: '',
    from: '',
    to: '',
  });
  // What was last sent to the server, so typing in the box does not refetch on
  // every keystroke — the search runs when it is asked to.
  const [applied, setApplied] = useState(filters);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: res } = await axios.get(
          api + '/api/users/admin/profile-edit/policy',
          { headers }
        );
        setFields(res.fields || []);
      } catch (error) {
        /* the dropdown is a convenience — the log still works without it */
      }
    };
    load();
  }, [headers]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      for (const [key, value] of Object.entries(applied)) {
        if (value) params.set(key, value);
      }
      const { data: res } = await axios.get(
        api + `/api/users/admin/profile-edit/history?${params}`,
        { headers }
      );
      setData(res);
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setLoading(false);
    }
  }, [applied, page, pageSize, headers]);

  useEffect(() => {
    load();
  }, [load]);

  const search = () => {
    setPage(1);
    setApplied(filters);
    // Keep the member in the URL so the page can be linked to and refreshed.
    const next = {};
    if (filters.username) next.username = filters.username;
    if (filters.userId) next.userId = filters.userId;
    setSearchParams(next, { replace: true });
  };

  const clear = () => {
    const empty = {
      q: '', username: '', userId: '', field: '', action: '', source: '', from: '', to: '',
    };
    setFilters(empty);
    setApplied(empty);
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  const set = (key) => (e) => setFilters({ ...filters, [key]: e.target.value });
  /** Dropdowns are a choice, not typing — they search immediately. */
  const setNow = (key) => (e) => {
    const next = { ...filters, [key]: e.target.value };
    setFilters(next);
    setApplied(next);
    setPage(1);
  };

  const activeCount = Object.values(applied).filter(Boolean).length;

  return (
    <div style={{ width: '87%', float: 'right', padding: '16px 20px 60px' }}>
      <Helmet>
        <title>Profile Edit History</title>
      </Helmet>
      <h3>Profile Edit History</h3>
      <p style={s.muted}>
        Every change to any member profile, newest first. Member edits spend one
        of their allowed edits; office edits never do.
      </p>

      {/* Filters */}
      <div style={s.filters}>
        <input
          style={{ ...s.select, minWidth: 240 }}
          placeholder="Search value, field, member or staff"
          value={filters.q}
          onChange={set('q')}
          onKeyDown={(e) => (e.key === 'Enter' ? search() : null)}
        />
        <input
          style={s.select}
          placeholder="Username"
          value={filters.username}
          onChange={set('username')}
          onKeyDown={(e) => (e.key === 'Enter' ? search() : null)}
        />
        <select style={s.select} value={filters.field} onChange={setNow('field')}>
          <option value="">All fields</option>
          {fields.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
        <select style={s.select} value={filters.action} onChange={setNow('action')}>
          <option value="">Anything that happened</option>
          {ACTIONS.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
        <select style={s.select} value={filters.source} onChange={setNow('source')}>
          <option value="">From anywhere</option>
          {SOURCES.map((src) => (
            <option key={src} value={src}>
              {src === 'app' ? 'Mobile app' : src === 'web' ? 'Website' : 'Admin panel'}
            </option>
          ))}
        </select>
        <input type="date" style={s.select} value={filters.from} onChange={setNow('from')} />
        <input type="date" style={s.select} value={filters.to} onChange={setNow('to')} />
        <button style={s.btn} onClick={search}>
          Search
        </button>
        {activeCount ? (
          <button style={s.ghostBtn} onClick={clear}>
            Clear
          </button>
        ) : null}
        <select
          style={s.select}
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n} per page
            </option>
          ))}
        </select>
      </div>

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>When</th>
              <th style={s.th}>Member</th>
              <th style={s.th}>Field</th>
              <th style={s.th}>From</th>
              <th style={s.th}>To</th>
              <th style={s.th}>What happened</th>
              <th style={s.th}>By</th>
              <th style={s.th}>Where</th>
              <th style={s.th}>Left</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td style={s.td} colSpan={9}>
                  Loading…
                </td>
              </tr>
            ) : data.rows.length === 0 ? (
              <tr>
                <td style={s.td} colSpan={9}>
                  Nothing matches these filters.
                </td>
              </tr>
            ) : (
              data.rows.map((row) => {
                const ac = actionColor(row.action);
                return (
                  <tr key={row._id}>
                    <td style={s.td}>{new Date(row.createdAt).toLocaleString()}</td>
                    <td style={s.td}>{row.username || '—'}</td>
                    <td style={s.td}>{row.label || row.field}</td>
                    <td style={{ ...s.td, maxWidth: 200, overflowWrap: 'anywhere' }}>
                      {row.from || '—'}
                    </td>
                    <td style={{ ...s.td, maxWidth: 200, overflowWrap: 'anywhere' }}>
                      {row.to || '—'}
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.pill, background: ac.bg, color: ac.color }}>
                        {ACTIONS.find((a) => a.value === row.action)?.label || row.action}
                      </span>
                    </td>
                    <td style={s.td}>{row.changedByName || '—'}</td>
                    <td style={s.td}>
                      {row.source === 'app'
                        ? 'Mobile app'
                        : row.source === 'admin'
                        ? 'Admin panel'
                        : 'Website'}
                    </td>
                    <td style={s.td}>
                      {row.remainingAfter === null || row.remainingAfter === undefined
                        ? '—'
                        : row.remainingAfter}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div style={s.pager}>
        <button style={s.btn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          ‹ Prev
        </button>
        <span style={{ margin: '0 10px' }}>
          Page {data.page} of {data.pages} ({data.count} change
          {data.count === 1 ? '' : 's'})
        </span>
        <button
          style={s.btn}
          disabled={page >= data.pages}
          onClick={() => setPage((p) => p + 1)}
        >
          Next ›
        </button>
      </div>
    </div>
  );
}

const s = {
  muted: { color: '#6b7280', fontSize: 13 },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' },
  select: { border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 14 },
  btn: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' },
  ghostBtn: { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 900 },
  th: { textAlign: 'left', padding: '9px 10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11, textTransform: 'uppercase', color: '#6b7280' },
  td: { padding: '9px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 13 },
  pill: { borderRadius: 999, padding: '2px 9px', fontSize: 12, fontWeight: 700 },
  pager: { marginTop: 14, display: 'flex', alignItems: 'center' },
};
